import { useCallback, useEffect, useRef, useState } from "react";

import { Drawer, Stack } from "@mui/material";
import { change, reduxForm, submit } from "redux-form";
import DateService from "utils/date.util";
import LocalStorage from "utils/localstorage.util";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import PersistReduxForm from "components/organisms/PersistReduxForm";
import ButtonLoading from "components/atoms/ButtonLoading";
import ObjectService from "utils/object.util";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import PaymentMethod from "components/templates/PaymentMethod/PaymentMethod";
import { MODAL, modalActions } from "redux/slices/modal";
import { preloadModal } from "components/templates/AppModal";
import {
  clearAggregatorPromoFormFields,
  getAggregatorDisplayCurrency,
  isAggregatorMcBooking,
} from "utils/aggregatorHourlyPricing.util";
import {
  isPartnerWebviewSession,
  isYeloAppSession,
  partnerHomePath,
  withPartnerToken,
} from "utils/partnerSession.util";
import { partnerUserPhoneFieldValue } from "utils/partnerUser.util";
import JourneyMap from "../JourneyMap/JourneyMap";
import useBookingTimeFormat from "hooks/useBookingTimeFormat";
import "./Checkout.scss";
import "styles/yeloplus-loading-button.scss";
import { primeKeyboard } from "utils/keyboard.util";

const pointFromPair = (pair: unknown) => {
  if (!Array.isArray(pair) || pair.length < 2) return null;
  const lat = Number(pair[0]);
  const lng = Number(pair[1]);
  return Number.isFinite(lat) && Number.isFinite(lng) ? { lat, lng } : null;
};

const CalendarIcon = () => (
  <svg className="ico" viewBox="0 0 24 24" aria-hidden="true">
    <rect x="3.5" y="5" width="17" height="15.5" rx="2.5" />
    <path d="M3.5 9.5h17M8 3v4M16 3v4" />
  </svg>
);

const ClockIcon = () => (
  <svg className="ico" viewBox="0 0 24 24" aria-hidden="true">
    <circle cx="12" cy="12" r="8.5" />
    <path d="M12 7.5V12l3 1.8" />
  </svg>
);

const PromoIcon = () => (
  <svg className="ico" viewBox="0 0 24 24" aria-hidden="true">
    <path d="M4 7.5A2.5 2.5 0 0 0 6.5 10 2.5 2.5 0 0 0 4 12.5V17h16v-4.5a2.5 2.5 0 0 0 0-5V3H4Z" />
    <path d="M13 7h3M13 11h3M13 15h3" />
  </svg>
);

const Checkout = ({ handleSubmit, form, onEditPassenger }: any) => {
  const { t } = useTranslation("translation");
  const { i18n } = useTranslation();
  const { format: timeFormat } = useBookingTimeFormat();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const user = useAppSelector((state) => state.auth.user);
  const selected = useAppSelector((state) => state.payment.selected);
  const submitLoader = useAppSelector((state) => state.booking.loading);
  const classsPrice = useAppSelector((state) => state.classes.classsPrice);
  const discount = useAppSelector(
    (state) => state.form?.[form]?.values?.discount
  );
  const discount_type = useAppSelector(
    (state) => state.form?.[form]?.values?.discount_type
  );
  const coupon_code = useAppSelector(
    (state) => state.form?.[form]?.values?.coupon_code
  );
  const is_yelo_points = useAppSelector(
    (state) => state.form?.[form]?.values?.is_yelo_points
  );
  const searchTripFormValues = useAppSelector(
    (state) => state.form?.PickUpForm?.values
  );
  const formDistance = useAppSelector(
    (state) => state.form?.SearchTripForm?.values?.distance
  );

  const additional_info = useAppSelector(
    (state) => state.form?.["AdditionalInfoForm"]?.values
  );

  let bookingInfo = searchTripFormValues;
  const bookingFormPayload = LocalStorage.getItem("BookingFormPayload");
  const isAggregatorBooking = isAggregatorMcBooking(bookingFormPayload);
  const isHourly = bookingFormPayload?.trip_type === "hourly";
  /* Same source order as FormHeader: the saved payload survives a reload,
     the live form value does not. */
  const distance = bookingFormPayload?.distance ?? formDistance;

  const formatPassengerPhone = (phoneValue: any) => {
    if (!phoneValue) return "";
    if (phoneValue.formattedValue) return phoneValue.formattedValue;
    if (phoneValue.data) return `+${phoneValue.value}`;
    const raw = `${phoneValue.value || phoneValue}`;
    return raw.startsWith("+") ? raw : `+${raw}`;
  };

  const [price_details, setPriceDetails] = useState({
    discount_amount: 0,
    total: classsPrice.price_details.brutto,
  });
  const [guestExpanded, setGuestExpanded] = useState(false);
  const [fareSummaryOpen, setFareSummaryOpen] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);
  const tripCardRef = useRef<HTMLDivElement>(null);
  const actionbarRef = useRef<HTMLDivElement>(null);

  /* The mobile review screen is one fixed composition: the map fills
     everything above the action bar, and the trip card floats at the bottom
     of the map. The bar's height changes with its content (coupon row,
     wrapping, safe-area inset), so it is measured rather than assumed — a
     hard-coded height let the page overflow and scroll on some phones. */
  useEffect(() => {
    const bar = actionbarRef.current;
    const formEl = formRef.current;
    if (!bar || !formEl || typeof ResizeObserver === "undefined") return;
    const sync = () =>
      formEl.style.setProperty("--yp-review-bar", `${bar.offsetHeight}px`);
    sync();
    const observer = new ResizeObserver(sync);
    observer.observe(bar);
    return () => observer.disconnect();
  }, []);

  /* The coupon sheet is opened from this screen; have its code ready, so it
     rises with the form in it rather than a spinner in an empty card. */
  useEffect(() => {
    preloadModal.APPLY_COUPON?.();
  }, []);

  /* Frame the route in the map area the overlays leave free: below the
     floating header, above the trip card. */
  const getReviewFitPadding = useCallback(() => {
    const header = document.querySelector(".flow-header") as HTMLElement | null;
    const card = tripCardRef.current;
    const gap = 28;
    return {
      top: (header?.offsetHeight || 64) + gap,
      bottom: (card ? card.offsetHeight + 16 : 200) + gap,
      left: 44,
      right: 44,
    };
  }, []);

  useEffect(() => {
    if (!user) return;
    dispatch(change(form, "title", user?.title));
    dispatch(change(form, "first_name", user?.first_name));
    dispatch(change(form, "last_name", user?.last_name));
    dispatch(change(form, "email", user?.email));

    const phoneField = partnerUserPhoneFieldValue(user);
    if (phoneField) {
      dispatch(change(form, "phone", phoneField));
    }

    dispatch(
      change(form, "pickup_sign", ObjectService.getFullName(user, false))
    );
  }, [user, dispatch, form]);

  useEffect(() => {
    const card_id = selected.card ? selected.card.card_id : "";
    dispatch(change(form, "card_id", card_id));
    dispatch(change(form, "payment_type", selected.payment_type));
  }, [selected, dispatch, form]);

  useEffect(() => {
    let total = classsPrice.price_details.brutto;
    let details = {
      total,
      discount_amount: 0,
    };
    let discount_amount =
      discount_type === "percentage" ? (total / 100) * discount : discount;
    let diff = details.total - discount_amount;

    if (discount_amount > 0) {
      if (diff < 0) {
        details.discount_amount = details.total;
        details.total = 0;
      } else {
        details.total = diff;
        details.discount_amount = discount_amount;
      }
    } else {
      details.total = details.total + details.discount_amount;
      details.discount_amount = 0;
    }

    setPriceDetails(details);
  }, [
    user,
    is_yelo_points,
    discount,
    discount_type,
    classsPrice.price_details.brutto,
  ]);

  useEffect(() => {
    dispatch(change(form, "class_id", classsPrice.class_id));
  }, [classsPrice, dispatch, form]);

  useEffect(() => {
    if (isAggregatorBooking) {
      clearAggregatorPromoFormFields(dispatch);
    }
  }, [dispatch, isAggregatorBooking]);

  useEffect(() => {
    if (!bookingInfo) return;

    for (const key in bookingInfo) {
      if (Object.prototype.hasOwnProperty.call(bookingInfo, key)) {
        const element = bookingInfo[key];
        dispatch(change(form, key, element));
      }
    }
  }, [dispatch, bookingInfo, form]);


  /* An airport pickup — the one case with a flight number, as in the
     passenger step (which remembers it across a reload in
     BookingRouteNames). */
  const savedPickupAirport = LocalStorage.getItem("BookingRouteNames")
    ?.pickupAirport;
  const airport = Boolean(
    searchTripFormValues?.pickup?.details?.airport ??
      (typeof savedPickupAirport === "boolean"
        ? savedPickupAirport
        : bookingFormPayload?.pickup_type === "airport")
  );
  const passengerPhone = formatPassengerPhone(
    additional_info?.phone || partnerUserPhoneFieldValue(user)
  );
  const pickupSign =
    additional_info?.pickup_sign || ObjectService.getFullName(user, false);
  const passengerName = [
    additional_info?.first_name,
    additional_info?.last_name,
  ]
    .filter(Boolean)
    .join(" ");
  /* After a reload the live form is empty; the places' names were saved
     with the search (BookingRouteNames, see PickUp.tsx) so the card names
     them instead of falling back to the full stored address. */
  const routeNames = LocalStorage.getItem("BookingRouteNames");
  const pickupName =
    searchTripFormValues?.pickup?.details?.name ||
    routeNames?.pickup ||
    bookingFormPayload?.pickup ||
    "";
  const destinationName =
    searchTripFormValues?.destination?.details?.name ||
    routeNames?.destination ||
    bookingFormPayload?.destination ||
    "";
  const hourlyDuration =
    searchTripFormValues?.duration || bookingFormPayload?.duration;
  const pickupPoint =
    pointFromPair(bookingFormPayload?.pickupCoordinates) ||
    searchTripFormValues?.pickup?.details?.geometry?.location;
  const destinationPoint = isHourly
    ? null
    : pointFromPair(bookingFormPayload?.destinationCoordinates) ||
      searchTripFormValues?.destination?.details?.geometry?.location;

  const openCoupon = () => {
    primeKeyboard();
    dispatch(
      modalActions.openModal({
        type: MODAL.APPLY_COUPON,
        data: coupon_code ? t("change") : t("apply"),
        width: "400px",
      })
    );
  };

  /* Passenger details are the step before this one, so editing them is a
     step back — SearchForm owns that navigation (and its history handling). */
  const editPassengerDetails = () => {
    setGuestExpanded(false);
    if (onEditPassenger) {
      onEditPassenger();
      return;
    }
    navigate(
      isPartnerWebviewSession()
        ? withPartnerToken("/search-form#additional-form")
        : "/search-form#additional-form"
    );
  };

  const currencySign = getAggregatorDisplayCurrency(
    classsPrice.price_details,
    isAggregatorBooking
  );
  /* MC (aggregator) fares are quoted as a single total — no net/VAT/discount
     breakdown is shown for them on this screen. */
  const showFareBreakdown = !isAggregatorBooking;
  const showPaymentBlock = !!bookingFormPayload?.isStripe && !isYeloAppSession();

  const renderFareRows = () => (
    <Stack gap={1.25}>
      {price_details.discount_amount > 0 ? (
        <>
          <div className="yp-review__row yp-review__row--muted">
            <span>{t("subtotalInclVat")}</span>
            <b className="yp-review__ltr">
              {classsPrice.price_details.brutto.toFixed?.(2)}{" "}
              {classsPrice.price_details.currency_sign}
            </b>
          </div>
          <div className="yp-review__row yp-review__row--muted">
            <span>
              {t("BooingDiscount")}
              {discount_type === "percentage" ? `( ${discount}% )` : ""}
            </span>
            <b className="yp-review__ltr">
              - {price_details.discount_amount.toFixed?.(2)}{" "}
              {classsPrice.price_details.currency_sign}
            </b>
          </div>
        </>
      ) : (
        <>
          {!(classsPrice.price_details.netto > 0) &&
            !(classsPrice.price_details.VAT > 0) && (
              <div className="yp-review__row yp-review__row--muted">
                <span>{t("subtotalInclVat")}</span>
                <b className="yp-review__ltr">
                  {classsPrice.price_details.brutto.toFixed?.(2)}{" "}
                  {classsPrice.price_details.currency_sign}
                </b>
              </div>
            )}
          {classsPrice.price_details.netto > 0 && (
            <div className="yp-review__row yp-review__row--muted">
              <span>{t("netPrices")}</span>
              <b className="yp-review__ltr">
                {classsPrice.price_details.netto.toFixed?.(2)}{" "}
                {classsPrice.price_details.currency_sign}
              </b>
            </div>
          )}
          {classsPrice.price_details.VAT > 0 && (
            <div className="yp-review__row yp-review__row--muted">
              <span style={{ textTransform: "uppercase" }}>
                {t("bookingVat")} ({classsPrice.price_details.VAT}%)
              </span>
              <b className="yp-review__ltr">
                {(
                  classsPrice.price_details.brutto -
                  classsPrice.price_details.netto
                ).toFixed?.(2)}{" "}
                {classsPrice.price_details.currency_sign}
              </b>
            </div>
          )}
        </>
      )}
    </Stack>
  );

  return (
    <form ref={formRef} onSubmit={handleSubmit} className="yp-review">
      <section className="yp-review__mobile-overview" aria-label={String(t("journeyDetails"))}>
        <div className="yp-review__map">
          {pickupPoint && (
            <JourneyMap
              variant="review"
              pickup={pickupPoint}
              destination={destinationPoint}
              getFitPadding={getReviewFitPadding}
            />
          )}
        </div>
        <div className="yp-review__trip-card" ref={tripCardRef}>
          <div className="yp-review__trip-top">
            <span className="yp-review__trip-when-wrap">
            <span className="yp-review__trip-when">
              <span>
                <span className="is-long">
                  {DateService.getDateString(bookingFormPayload?.time_stamp)}
                </span>
                <span className="is-short">
                  {DateService.getShortDateString(bookingFormPayload?.time_stamp)}
                </span>
              </span>
              <span className="yp-review__trip-time">
                {DateService.getDisplayTimeString(
                  bookingFormPayload?.time_stamp,
                  timeFormat
                )}
              </span>
            </span>
            </span>
            <button
              type="button"
              className="yp-review__trip-guest"
              aria-label={String(t("guest"))}
              aria-haspopup="dialog"
              onClick={() => setGuestExpanded(true)}
            >
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <circle cx="12" cy="8" r="3.2" />
                <path d="M5 20c.4-4.1 2.8-6.2 7-6.2s6.6 2.1 7 6.2" />
              </svg>
              <span>{passengerName || passengerPhone}</span>
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="m6 9 6 6 6-6" />
              </svg>
            </button>
          </div>
          {/* Pickup and destination side by side, joined by a rail that uses
              the map's pin vocabulary (purple dot = pickup, yellow square =
              destination) with the distance riding in the middle. Kept
              horizontal so the card stays short over the map. */}
          <div className="yp-review__route">
            <div className="yp-review__route-rail" aria-hidden="true">
              <span className="yp-review__stop-mark is-pickup" />
              <span className="yp-review__route-line" />
              {!isHourly && distance ? (
                <span className="yp-review__stop-chip">
                  <svg viewBox="0 0 28 24">
                    <path d="m5 16 2-5.2a3 3 0 0 1 2.8-2h8.4a3 3 0 0 1 2.8 2L23 16v3H5v-3Z" />
                    <path d="M4 15h20M8 19v2m12-2v2" />
                  </svg>
                  <span className="yp-review__ltr">{distance} {t("km")}</span>
                </span>
              ) : null}
              <span className="yp-review__route-line" />
              <span
                className={`yp-review__stop-mark ${isHourly ? "is-duration" : "is-dropoff"}`}
              />
            </div>
            <div className="yp-review__route-ends">
              <div className="yp-review__stop-copy">
                <small>{t("pickUp")}</small>
                <strong>{pickupName}</strong>
              </div>
              <div className="yp-review__stop-copy is-end">
                <small>{isHourly ? t("duration") : t("destination")}</small>
                <strong>
                  {isHourly ? `${hourlyDuration} ${t("hours")}` : destinationName}
                </strong>
                {isHourly && hourlyDuration ? (
                  <span>
                    {t("incl")} {Number(hourlyDuration) * 40} {t("km")}
                  </span>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Vehicle + journey legs + date/time folded into one card — was three
          separate boxes plus a two-chip date/time row. */}
      <div className="yp-review__card yp-review__journey">
        <div className="yp-review__journey-head">
          <span className="yp-review__kicker" style={{ margin: 0 }}>
            {t("journeyDetails")}
          </span>
        </div>

        <div className="yp-review__leg">
          <span className="yp-review__pin">
            <span className="yp-review__pin-line" aria-hidden="true" />
            <b />
          </span>
          <div className="yp-review__leg-row">
            <div className="yp-review__route-top">
              <small className="yp-review__route-label">{t("pickUp")}</small>
              {!isHourly && distance ? (
                <span className="yp-review__route-metrics">
                  {distance} {t("km")}
                </span>
              ) : null}
            </div>
            <strong className="yp-review__leg-title">
              {searchTripFormValues?.pickup?.details?.name}
            </strong>
            <em className="yp-review__leg-sub">
              {searchTripFormValues?.pickup?.value}
            </em>
          </div>

          <span className={`yp-review__pin is-end${isHourly ? " is-hourly" : ""}`}>
            <b />
          </span>
          <div className="yp-review__leg-row">
            <small className="yp-review__route-label">
              {isHourly ? t("duration") : t("destination")}
            </small>
            <strong className="yp-review__leg-title">
              {isHourly
                ? `${searchTripFormValues?.duration} ${t("hours")}`
                : searchTripFormValues?.destination?.details?.name}
            </strong>
            <em className="yp-review__leg-sub">
              {isHourly
                ? `${t("incl")} ${searchTripFormValues?.duration * 40} ${t(
                    "km"
                  )}`
                : searchTripFormValues?.destination?.value}
            </em>
          </div>
        </div>

        {!isHourly && (
          <div className="yp-review__whenrow">
            <div>
              <CalendarIcon />
              {DateService.getShortDateString(bookingFormPayload.time_stamp)}
            </div>
            <div>
              <ClockIcon />
              {DateService.getDisplayTimeString(
                bookingFormPayload.time_stamp,
                timeFormat
              )}
            </div>
          </div>
        )}

        <span className="yp-review__kicker yp-review__kicker--muted">
          {t("yourVehicle")}
        </span>
        <div className="yp-review__veh">
          <span className="yp-review__veh-media">
            <img
              src={classsPrice.class_image}
              alt=""
              style={{ objectFit: "contain" }}
            />
          </span>
          <span>
            <strong>{classsPrice.class_name}</strong>
            <em>{classsPrice.vehicle_name}</em>
          </span>
        </div>
      </div>

      <div className="yp-review__card yp-review__guest">
        <button
          type="button"
          className="yp-review__guest-toggle"
          aria-expanded={guestExpanded}
          onClick={() => setGuestExpanded((expanded) => !expanded)}
        >
          <span>
            <strong>{t("guest")}</strong>
            <small>{passengerName || passengerPhone}</small>
          </span>
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="m6 9 6 6 6-6" />
          </svg>
        </button>
        <div className={`yp-review__guest-content${guestExpanded ? " is-open" : ""}`}>
          <span className="yp-review__kicker" style={{ margin: 0 }}>
            {t("guest")}
          </span>

        {passengerName && (
          <>
            <small className="yp-review__route-label yp-review__guest-label">
              {t("passengerName")}
            </small>
            <p className="yp-review__guest-value">{passengerName}</p>
          </>
        )}

        <small
          className="yp-review__route-label yp-review__guest-label"
        >
          {t("phone")}
        </small>
        <p className="yp-review__guest-value yp-review__ltr">
          {passengerPhone}
        </p>

        {additional_info?.email && (
          <>
            <small className="yp-review__route-label yp-review__guest-label">
              {t("email")}
            </small>
            <p className="yp-review__guest-value yp-review__ltr">
              {additional_info.email}
            </p>
          </>
        )}

        <small
          className="yp-review__route-label yp-review__guest-label"
        >
          {t("pickUpSign")}
        </small>
        <p className="yp-review__guest-value">{pickupSign}</p>

        {airport && (
          <>
            <small className="yp-review__route-label yp-review__guest-label">
              {t("Flight Number")}
            </small>
            <p className="yp-review__guest-value">
              {additional_info?.flight_number}
            </p>
          </>
        )}
        </div>
      </div>

      {(showFareBreakdown || showPaymentBlock) && (
      <div className="yp-review__card yp-review__checkout">
        {showPaymentBlock && (
          <div className="yp-review__payment-block">
            <span className="yp-review__kicker" style={{ margin: "0 0 14px" }}>
              {t("paymentMethod")}
            </span>
            <PaymentMethod />
          </div>
        )}
        {showFareBreakdown && (
          <>
            <span className="yp-review__kicker" style={{ margin: "0 0 14px" }}>
              {t("fareDetails")}
            </span>

            {renderFareRows()}
          </>
        )}

        {!isAggregatorBooking && (
          <button
            type="button"
            className="yp-review__promo"
            aria-expanded={!!coupon_code}
            onClick={openCoupon}
          >
            <span className="yp-review__promo-icon">
              <PromoIcon />
            </span>
            <span className="yp-review__promo-copy">
              {coupon_code ? t("change") : t("add")}{" "}
              {i18n.language === "ar"
                ? `${t("coupon")} ${t("discountCoupon")}`
                : t("coupon")}
            </span>
            <span className="yp-review__promo-plus">+</span>
          </button>
        )}

      </div>
      )}

      {/* Fixed bottom bar, same native-style treatment as the vehicle-class
          step's action bar: plain total row + full-width CTA pinned to the
          screen, instead of a total/button embedded in the scrolling card. */}
      <div className="yp-review__actionbar" ref={actionbarRef}>
        <div className="yp-review__mobile-actionbar-head">
          <div className="yp-review__actionbar-class">
            <strong>{classsPrice.class_name}</strong>
            <small>{classsPrice.vehicle_name}</small>
          </div>
          {showFareBreakdown ? (
            <button
              type="button"
              className="yp-review__mobile-total"
              aria-label={String(t("viewPaymentSummary"))}
              aria-haspopup="dialog"
              onClick={() => setFareSummaryOpen(true)}
            >
              <span className="yp-review__ltr">
                {currencySign} {price_details.total.toFixed?.(2)}
              </span>
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <circle cx="12" cy="12" r="9" />
                <path d="M12 11v5M12 8h.01" />
              </svg>
            </button>
          ) : (
            <span className="yp-review__mobile-total">
              <span className="yp-review__ltr">
                {currencySign} {price_details.total.toFixed?.(2)}
              </span>
            </span>
          )}
        </div>
        <div className="yp-review__actionbar-total">
          {showFareBreakdown ? (
            <button
              type="button"
              className="yp-review__total-info"
              aria-label={String(t("viewPaymentSummary"))}
              aria-haspopup="dialog"
              onClick={() => setFareSummaryOpen(true)}
            >
              <span className="yp-review__actionbar-label">
                {t("total")} {t("price")}
              </span>
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <circle cx="12" cy="12" r="9" />
                <path d="M12 11v5M12 8h.01" />
              </svg>
            </button>
          ) : (
            <span className="yp-review__actionbar-label">
              {t("total")} {t("price")}
            </span>
          )}
          <span className="yp-review__actionbar-amount">
            <em>
              {getAggregatorDisplayCurrency(
                classsPrice.price_details,
                isAggregatorBooking
              )}
            </em>
            {price_details.total.toFixed?.(2)}
          </span>
        </div>

        {!isAggregatorBooking && (
          <button
            type="button"
            className={`yp-review__coupon-row${coupon_code ? " is-applied" : ""}`}
            aria-haspopup="dialog"
            onClick={openCoupon}
          >
            <span className="yp-review__coupon-icon">
              <PromoIcon />
            </span>
            <span className="yp-review__coupon-copy">
              {coupon_code ? (
                <>
                  <strong className="yp-review__ltr">{coupon_code}</strong>
                  <small>
                    {t("couponApplied")}
                    {price_details.discount_amount > 0 && (
                      <span className="yp-review__ltr">
                        {" · -"}
                        {currencySign} {price_details.discount_amount.toFixed?.(2)}
                      </span>
                    )}
                  </small>
                </>
              ) : (
                <>
                  <strong>{t("applyCoupon")}</strong>
                  <small>{t("couponHint")}</small>
                </>
              )}
            </span>
            <span className="yp-review__coupon-action">
              {coupon_code ? t("change") : t("add")}
            </span>
          </button>
        )}

        <ButtonLoading
          /* yelo-usernext's loading face (styles/yeloplus-loading-button). */
          className={`yp-review__submit yp-btn-loading${
            submitLoader ? " is-loading" : ""
          }`}
          disableElevation
          disabled={submitLoader}
          aria-busy={submitLoader || undefined}
          onClick={() => dispatch(submit("SearchForm"))}
        >
          {submitLoader ? (
            <span className="book-now__loading" aria-live="polite">
              <span className="book-now__loading-text">
                {(t("loading") || "Loading...").replace(/\.+$/, "")}
              </span>
              <span className="book-now__dots" aria-hidden="true">
                <span />
                <span />
                <span />
              </span>
            </span>
          ) : (
            t("bookNow")
          )}
        </ButtonLoading>
      </div>

      <Drawer
        anchor="bottom"
        open={guestExpanded}
        onClose={() => setGuestExpanded(false)}
        className="yp-review__fare-drawer yp-review__detail-drawer"
        PaperProps={{
          role: "dialog",
          "aria-modal": true,
          "aria-labelledby": "yp-review-guest-title",
        }}
      >
        <div className="yp-review__fare-sheet">
          <div className="yp-review__fare-sheet-head">
            <h2 id="yp-review-guest-title">{t("guest")}</h2>
            <button type="button" aria-label={String(t("close"))} onClick={() => setGuestExpanded(false)}>
              <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 5l14 14M19 5 5 19" /></svg>
            </button>
          </div>
          <dl className="yp-review__detail-list">
            {/* Each pair is wrapped so a tablet can lay them out as tiles; on
                a phone the wrapper is display:contents and the list stays a
                plain two-column grid. */}
            {passengerName && <div className="yp-review__detail-item"><dt>{t("passengerName")}</dt><dd>{passengerName}</dd></div>}
            <div className="yp-review__detail-item"><dt>{t("phone")}</dt><dd className="yp-review__ltr">{passengerPhone}</dd></div>
            {additional_info?.email && <div className="yp-review__detail-item"><dt>{t("email")}</dt><dd className="yp-review__ltr">{additional_info.email}</dd></div>}
            {pickupSign && <div className="yp-review__detail-item"><dt>{t("pickUpSign")}</dt><dd>{pickupSign}</dd></div>}
            {airport && additional_info?.flight_number && <div className="yp-review__detail-item"><dt>{t("Flight Number")}</dt><dd>{additional_info.flight_number}</dd></div>}
          </dl>
          <button className="yp-review__detail-edit" type="button" onClick={editPassengerDetails}>
            {t("edit")}
          </button>
        </div>
      </Drawer>

      <Drawer
        anchor="bottom"
        open={showFareBreakdown && fareSummaryOpen}
        onClose={() => setFareSummaryOpen(false)}
        className="yp-review__fare-drawer"
        PaperProps={{
          role: "dialog",
          "aria-modal": true,
          "aria-labelledby": "yp-review-fare-title",
        }}
      >
        <div className="yp-review__fare-sheet">
          <div className="yp-review__fare-sheet-head">
            <h2 id="yp-review-fare-title">{t("paymentSummary")}</h2>
            <button
              type="button"
              aria-label={String(t("close"))}
              onClick={() => setFareSummaryOpen(false)}
            >
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M5 5l14 14M19 5 5 19" />
              </svg>
            </button>
          </div>
          {renderFareRows()}
          <div className="yp-review__fare-sheet-total">
            <span>{t("total")} {t("price")}</span>
            <strong className="yp-review__ltr">
              {getAggregatorDisplayCurrency(
                classsPrice.price_details,
                isAggregatorBooking
              )} {price_details.total.toFixed?.(2)}
            </strong>
          </div>
        </div>
      </Drawer>

      <PersistReduxForm form={form} />
    </form>
  );
};

export default reduxForm<{}, { onEditPassenger?: () => void }>({
  form: "SearchForm",
  destroyOnUnmount: false,
})(Checkout);
